import { CartLinesUpdateEvent } from '@shopify/events';

/*!
 * Vertical Oceans — shipping-fee cart engine.
 * Ports docs/shipping-item.md (old theme assets/shipping-fee-cart.js) to Horizon.
 *
 * Adds hidden "Shipping Fee" line item(s) whose total = the per-delivery-date fee for
 * each unique delivery/harvest date, so the fee flows to checkout as a real charge
 * (cart attributes can't charge; discounts can't add a fee). The fee item is marked
 * with properties._shipping_fee = 'true' so the theme hides it from the cart line
 * list (snippets/cart-products.liquid) and this engine hides it from the
 * header/count. Source of truth = line_item.properties['Delivery Date' |
 * 'Harvest Date'], written by the PDP date picker.
 *
 * TWO fee variants — One-Time Purchase vs Subscription (config.oneTime /
 * config.subscription). Each unique date is charged by whether THAT date's items are
 * subscription or one-time. A date's mode = 'one-time' if ANY item on that date is a
 * one-time purchase, else 'subscription' (one-time wins on a mixed date). One-Time
 * dates are added as the One-Time variant line; subscription dates as the
 * Subscription variant line — so checkout can show up to two "Shipping Fee" lines,
 * each at its own per-date price, with the dates + amount + mode listed as line-item
 * properties. Free shipping (merch subtotal ≥ threshold) removes all fee lines.
 *
 * Horizon integration: listens to `shopify:cart:lines-update` (the theme's cart
 * event bus), fetches /cart.js, and mutates the fee items via /cart/add.js,
 * /cart/change.js, /cart/update.js. Config comes from the global JSON block emitted
 * by snippets/vo-shipping-fee-config.liquid (rendered in <head> so this runs on
 * every page, including PDP add-to-cart). The summary UI shell
 * (snippets/shipping-fee-summary.liquid) is only in the cart; this engine populates
 * any [data-shipping-fee-summary] it finds.
 */
(function () {
  'use strict';

  var PROPERTY_NAMES = ['Delivery Date', 'Harvest Date'];
  var SHIPPING_FEE_MARKER = '_shipping_fee';
  // Visible mode label shown next to each date in the fee line's properties + summary.
  var MODE_LABELS = { 'one-time': 'One Time Purchase', 'subscription': 'Subscription' };
  var _syncing = false;
  var _debounceTimer = null;
  // Set when a cart change arrives WHILE a fee sync is in flight (see the lines-update
  // listener). finish()/fail() then re-fetch the latest cart and re-sync, so a rapid
  // burst of changes (e.g. +1, +1, +1) always ends with fees/summary/bubble matching
  // the FINAL cart — instead of the second change being silently dropped.
  var _pendingResync = false;
  var _config = null;
  var _lastAttributeKeys = [];

  // ---- Config ----
  function getConfig() {
    if (_config) return _config;
    var el = document.querySelector('[data-shipping-fee-config]');
    if (!el) return null;
    try {
      _config = JSON.parse(el.textContent);
    } catch (e) {
      _config = null;
    }
    return _config;
  }

  // ---- Helpers ----
  var MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function pad(n) { return String(n).padStart(2, '0'); }

  // Parse a display date ("5 Aug 2025") into ISO "2025-08-05".
  function parseDate(display) {
    if (!display || typeof display !== 'string') return null;
    var parts = display.trim().split(/\s+/);
    if (parts.length !== 3) return null;
    var day = parseInt(parts[0], 10);
    var mi = MONTH_NAMES.indexOf(parts[1]);
    var year = parseInt(parts[2], 10);
    if (!day || mi < 0 || !year) return null;
    return year + '-' + pad(mi + 1) + '-' + pad(day);
  }

  // Format an ISO date ("2025-08-05") for display as "Aug 05 2025" (Month-DD-YYYY).
  function formatDisplayDate(iso) {
    if (!iso) return '';
    var p = iso.split('-');
    if (p.length !== 3) return iso;
    var mi = parseInt(p[1], 10) - 1;
    if (mi < 0 || mi > 11) return iso;
    return MONTH_NAMES[mi] + ' ' + p[2] + ' ' + p[0];
  }

  // /cart.js returns HTML-entity-encoded titles; decode before building checkout strings.
  function decodeHtml(str) {
    if (!str) return '';
    var tx = document.createElement('textarea');
    tx.innerHTML = str;
    return tx.value;
  }

  function formatMoney(cents, symbol) {
    return symbol + (cents / 100).toFixed(2);
  }

  // Per-date fee label, price-first: "S$5.00 for One Time Purchase". A $0 fee is
  // free shipping for that mode, so it reads "Free" — never "S$0.00 for ...".
  function formatFeeLabel(cents, mode, symbol) {
    if (!cents) return 'Free';
    return formatMoney(cents, symbol) + ' for ' + (MODE_LABELS[mode] || '');
  }

  function cartPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  // Collect every cart-items-component section id on the page (drawer + /cart) so a
  // fee mutation can ask Shopify to render those sections back and the theme can morph
  // them (line items, totals, checkout recap) — the same pattern vo-cart-date-guard.js
  // uses. Without sections the drawer would stay stale until a full reload.
  function getSectionsInfo() {
    var ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (c) {
      if (c.dataset.sectionId) ids.add(c.dataset.sectionId);
    });
    return { sections: Array.from(ids).join(','), sections_url: window.location.pathname };
  }

  // A cart line is a subscription when it carries a selling plan (present ⇒ sub).
  // /cart.js exposes `selling_plan_allocation` (object) on subscription lines; older
  // payloads also include `selling_plan_id`. The theme adds subs with `selling_plan`
  // (assets/vo-cart-offer.js) so this detects them reliably.
  function isSubscriptionLine(item) {
    if (!item) return false;
    return !!item.selling_plan_allocation || !!item.selling_plan_id;
  }

  // ---- Read dates & compute fee ----
  function extractUniqueDates(items, config) {
    var byIso = {};
    var order = [];
    var configuredFeeVariantIds = feeVariantIds(config);
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var isMarkedFee = item.properties && item.properties[SHIPPING_FEE_MARKER];
      var isConfiguredFeeVariant = configuredFeeVariantIds.indexOf(String(item.variant_id || '')) !== -1;
      if (!item.properties || isMarkedFee || isConfiguredFeeVariant) continue;
      var sub = isSubscriptionLine(item);
      for (var p = 0; p < PROPERTY_NAMES.length; p++) {
        var raw = item.properties[PROPERTY_NAMES[p]];
        if (!raw) continue;
        var iso = parseDate(raw);
        if (!iso) continue;
        if (!byIso[iso]) {
          byIso[iso] = { key: iso, display: formatDisplayDate(iso), products: [], hasSub: false, hasOneTime: false };
          order.push(iso);
        }
        var rec = byIso[iso];
        if (sub) rec.hasSub = true; else rec.hasOneTime = true;
        var title = decodeHtml(item.product_title || item.title || '');
        if (title && rec.products.indexOf(title) === -1) rec.products.push(title);
      }
    }
    order.sort();
    return order.map(function (iso) {
      var rec = byIso[iso];
      // One-time wins on a mixed date: if ANY item on the date is one-time, charge
      // the One-Time fee variant for that date.
      rec.mode = rec.hasOneTime ? 'one-time' : 'subscription';
      return rec;
    });
  }

  function modePrice(config, mode) {
    // NOTE: test with `!= null`, not truthiness — a $0 fee (free shipping for a mode)
    // is a valid price and must NOT fall through to the other mode's price.
    if (mode === 'subscription' && config.subscription && config.subscription.feePerDate != null) return config.subscription.feePerDate;
    if (config.oneTime && config.oneTime.feePerDate != null) return config.oneTime.feePerDate;
    return config.feePerDate || 0;
  }

  function calculateFee(uniqueDates, config, merchSubtotalCents) {
    var free = config.freeShipThresholdCents > 0 && merchSubtotalCents >= config.freeShipThresholdCents;
    var otDates = [], subDates = [];
    for (var i = 0; i < uniqueDates.length; i++) {
      if (uniqueDates[i].mode === 'subscription') subDates.push(uniqueDates[i]);
      else otDates.push(uniqueDates[i]);
    }
    return {
      free: free,
      dateCount: uniqueDates.length,
      oneTime: { dateCount: otDates.length, dates: otDates, amount: free ? 0 : otDates.length * modePrice(config, 'one-time') },
      subscription: { dateCount: subDates.length, dates: subDates, amount: free ? 0 : subDates.length * modePrice(config, 'subscription') },
    };
  }

  function feeVariantIds(config) {
    var ids = [];
    var candidates = [
      config && config.oneTime && config.oneTime.variantId,
      config && config.subscription && config.subscription.variantId,
      config && config.variantId,
    ];
    for (var i = 0; i < candidates.length; i++) {
      var id = String(candidates[i] || '');
      if (id && id !== '0' && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  // Treat the configured fee variants as fee lines even if their private marker was
  // lost or never attached. Without this fallback, a bare Shipping Fee product is
  // counted as merchandise and survives beside the correctly managed fee line.
  function findFeeItems(items, config) {
    var variantIds = feeVariantIds(config);
    return items.filter(function (item) {
      var marked = item.properties && item.properties[SHIPPING_FEE_MARKER] === 'true';
      return marked || variantIds.indexOf(String(item.variant_id || '')) !== -1;
    });
  }

  // Legacy/unmarked fee lines have no _fee_mode. Infer their mode from the configured
  // variant so they can be adopted or removed during reconciliation. When both modes
  // use one variant, buildTargets merges them into the one-time target.
  function feeItemMode(item, config) {
    var explicit = item.properties && item.properties._fee_mode;
    if (explicit) return explicit;
    var id = String(item.variant_id || '');
    var oneTimeId = String((config.oneTime && config.oneTime.variantId) || config.variantId || '');
    var subscriptionId = String((config.subscription && config.subscription.variantId) || oneTimeId || '');
    if (id && id === oneTimeId) return 'one-time';
    if (id && id === subscriptionId) return 'subscription';
    return '';
  }

  function feeLineTotal(feeItems) {
    return feeItems.reduce(function (s, i) { return s + (i.final_line_price || i.line_price || 0); }, 0);
  }

  // ---- Build fee properties (per-mode; date keys carry amount + mode label) ----
  // Each fee line lists only ITS mode's dates. The visible property value is
  // "S$<amount> — <Mode label>" so checkout shows, e.g.:
  //   Aug 05 2026: S$5.00 — One Time Purchase
  function buildFeeProperties(dates, targetMode, feePerDate, config) {
    var props = {};
    var symbol = config.currencySymbol || '';
    props[SHIPPING_FEE_MARKER] = 'true';
    props._fee_mode = targetMode;
    props._date_breakdown = dates.length + ' date(s) × ' + formatMoney(feePerDate || 0, symbol);
    props._unique_dates = dates.map(function (d) { return d.display; }).join(', ');

    var mode = config.checkoutDisplay || 'date-products';
    if (mode === 'off') return props;
    if (mode === 'date-only') {
      props['Delivery dates'] = props._unique_dates;
      return props;
    }
    for (var i = 0; i < dates.length; i++) {
      var d = dates[i];
      // Label each date by ITS OWN mode (so a merged fallback line still shows the
      // correct per-date mode even when the line's variant is one-time). Price-first
      // ("S$5.00 for One Time Purchase"); a $0 fee reads "Free".
      props[d.display] = formatFeeLabel(feePerDate || 0, d.mode, symbol);
    }
    return props;
  }

  function propsMatch(item, expected) {
    if (!item.properties) return false;
    var keys = Object.keys(expected);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (String(item.properties[k] || '') !== String(expected[k] || '')) return false;
    }
    // No stale visible (non-underscore) keys beyond what we expect.
    var visExp = keys.filter(function (k) { return k.charAt(0) !== '_'; });
    var visAct = Object.keys(item.properties).filter(function (k) { return k.charAt(0) !== '_'; });
    if (visAct.length !== visExp.length) return false;
    return true;
  }

  // ---- Build sync targets (one per mode that has dates, merged when same variant) ----
  function buildTargets(fee, config) {
    if (fee.free) return [];
    var oneTime = config.oneTime || { variantId: config.variantId, feePerDate: config.feePerDate };
    var subscription = config.subscription || oneTime;
    var sameVariant = !!oneTime.variantId && !!subscription.variantId && oneTime.variantId === subscription.variantId;
    var otDates = fee.oneTime.dates;
    var subDates = fee.subscription.dates;
    var targets = [];
    if (otDates.length || (sameVariant && subDates.length)) {
      var merged = sameVariant ? otDates.concat(subDates) : otDates;
      targets.push({ mode: 'one-time', variantId: oneTime.variantId, feePerDate: oneTime.feePerDate, qty: merged.length, dates: merged });
    }
    if (!sameVariant && subDates.length) {
      targets.push({ mode: 'subscription', variantId: subscription.variantId, feePerDate: subscription.feePerDate, qty: subDates.length, dates: subDates });
    }
    return targets;
  }

  // ---- syncFeeProduct — multi-target line-item integration ----
  // Adds / updates / removes the fee line(s) so each targeted variant has one line with
  // the right qty + properties, then dispatches a single CartLinesUpdateEvent carrying
  // the freshly rendered cart sections so the theme morphs the drawer's line items,
  // totals, and checkout recap (which are SRA-morphed server-side) — otherwise the
  // drawer would stay stale until a full page reload. Mirrors vo-cart-date-guard.js#remove.
  // Returns a promise resolving to true if any fee line was mutated.
  function syncFeeProduct(cart, uniqueDates, fee, feeItems, config) {
    var targets = buildTargets(fee, config);

    // Group every recognized fee line by mode. Keep arrays rather than a single lookup:
    // multiple lines for one mode are precisely the invalid state we must repair.
    var byMode = {};
    feeItems.forEach(function (item) {
      var mode = feeItemMode(item, config);
      if (!byMode[mode]) byMode[mode] = [];
      byMode[mode].push(item);
    });

    // Select at most one keeper for each target mode. Prefer a completely correct line;
    // otherwise adopt one line on the correct variant and rewrite its qty/properties.
    // Every other recognized fee line is removed before adds/updates run.
    var sectionsInfo = getSectionsInfo();
    var keepKeys = [];
    var targetOps = [];
    targets.forEach(function (t) {
      var expectedProps = buildFeeProperties(t.dates, t.mode, t.feePerDate, config);
      var candidates = byMode[t.mode] || [];
      var exact = candidates.find(function (item) {
        return item.variant_id === t.variantId && item.quantity === t.qty && propsMatch(item, expectedProps);
      });
      var existing = exact || candidates.find(function (item) { return item.variant_id === t.variantId; });
      if (existing) {
        keepKeys.push(existing.key);
        if (!exact) {
          targetOps.push({ url: Theme.routes.cart_change_url, body: { id: existing.key, quantity: t.qty, properties: expectedProps } });
        }
      } else {
        targetOps.push({ url: Theme.routes.cart_add_url, body: { id: t.variantId, quantity: t.qty, properties: expectedProps } });
      }
    });

    var removeOps = [];
    feeItems.forEach(function (item) {
      if (keepKeys.indexOf(item.key) === -1) {
        removeOps.push({ url: Theme.routes.cart_change_url, body: { id: item.key, quantity: 0 } });
      }
    });
    var ops = removeOps.concat(targetOps);

    if (!ops.length) return Promise.resolve(false); // nothing to change → no event, no write

    // Stamp every op with sections so the LAST response carries fresh section HTML.
    // Only attach when there's at least one cart-items-component on the page — an empty
    // `sections` value is an invalid value Shopify rejects with HTTP 400 (per the Cart
    // API docs). When none is present (e.g. Buy Now on a PDP, drawer closed) there is
    // nothing to morph anyway, so we just mutate the cart without sections.
    ops.forEach(function (op) {
      if (sectionsInfo.sections) {
        op.body.sections = sectionsInfo.sections;
        op.body.sections_url = sectionsInfo.sections_url;
      }
    });

    _syncing = true;
    var deferred = CartLinesUpdateEvent.createPromise();
    // Announce up front so the cart-items-component + vo-cart-drawer await the morph, and
    // so this engine's own listener bails on re-entry (it checks _syncing, which is true
    // until finish()).
    //
    // The Shopify Standard Events validator requires `lines` to have at least one item
    // (minItems: 1) — an empty array FAILS validation ("Too small: expected array to have
    // >=1 items") and REJECTS the event's promise, so no consumer's `.then` runs and the
    // drawer never morphs. This most visibly breaks the empty state: when the last merch
    // line is removed this engine removes the orphan fee line, but with `lines: []` the
    // promise rejected and the drawer kept a ghost row instead of showing the empty state.
    // Pass the actual fee-line changes (line key for change/remove, variant id for add) so
    // the payload validates and the morph runs. `action: 'update'` (not 'add') still avoids
    // the drawer's auto-open + loading-overlay path. See [[cart-drawer-audit-findings]]
    // (empty-state morph).
    var lines = ops
      .map(function (op) {
        return { id: String(op.body.id || ''), quantity: Number(op.body.quantity) || 0 };
      });
    // The Standard Events validator requires each `lines[].quantity` to be > 0, but fee
    // removals carry quantity: 0 (and pure removals are the common case when the last
    // merch line is removed and this engine clears the orphan fee). The `lines` payload
    // is ceremonial here — the morph is driven by `sections` in the resolved promise, not
    // by `lines` (the cart-items-component / cart-drawer read `detail.sections`, and
    // cart-drawer keys its auto-open off `event.action === 'add'`, not `lines`). So clamp
    // each entry's quantity to a positive value so the payload validates and the promise
    // resolves, letting the empty-state morph run. See [[cart-drawer-audit-findings]].
    var validLines = lines.map(function (l) {
      return { id: l.id || 'vo-shipping-fee', quantity: l.quantity > 0 ? l.quantity : 1 };
    });
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: validLines,
        promise: deferred.promise,
      })
    );

    // Re-fill the custom summary shell AFTER the theme's morph listeners run. Those
    // listeners register on the same promise DURING dispatch (so before this .then),
    // and morphSection's DOM patch is synchronous, so this fulfilled handler is
    // guaranteed to fire after the morph. The drawer leaves the shell un-morphed
    // (it's chrome), but on /cart the full-mode morph REPLACES the shell — so a
    // synchronous re-fill here would be erased; the .then runs after, correcting it.
    // `last` is hoisted (var) and set by step() before we resolve, so the closure sees it.
    var last = null;
    deferred.promise.then(function () {
      if (last) processCart(last, { renderOnly: true });
    });

    // Run ops one at a time; the LAST response holds the final cart + sections, which the
    // cart-items-components morph from when we resolve the event.
    var i = 0;
    function step() {
      if (i >= ops.length) return finish();
      var op = ops[i++];
      cartPost(op.url, op.body)
        .then(function (res) {
          last = res;
          if (res && res.errors) throw new Error(res.errors);
          step();
        })
        .catch(fail);
    }
    function fail(err) {
      _syncing = false;
      try { deferred.reject(err); } catch (e) { /* already settled */ }
      console.warn('[shipping-fee] sync failed', err);
      resyncIfPending();
    }
    function finish() {
      _syncing = false;
      // A customer cart change happened while these fee requests were in flight. Their
      // section HTML represents an older cart, so publishing it now can visually restore
      // an item that was just removed. Resolve without the stale sections; the theme's
      // cart listeners will fetch the current section while resyncIfPending reconciles
      // the fee against the latest server cart.
      if (_pendingResync) {
        last = null;
        deferred.resolve({ detail: { source: 'vo-shipping-fee', didError: false } });
        resyncIfPending();
        return;
      }
      if (!last) {
        deferred.resolve({ detail: { source: 'vo-shipping-fee', didError: false } });
        resyncIfPending();
        return;
      }
      // Resolve with a *normalized* cart, not the raw AJAX response. The Standard Events
      // promise wrapper calls `normalizeCartSummary(result.cart)`, which destructures
      // `{ id, lines, ...rest }` and maps over `lines` — but the raw `/cart/change.js`
      // response exposes `items` (not `lines`), so `lines` is undefined and `.map` throws
      // ("Cannot read properties of undefined (reading 'map')"), rejecting the promise and
      // blocking the drawer morph (most visibly the empty state). The cart-items-component
      // avoids this by passing `createCartFromAjaxResponse(...)` (items→lines); do the same.
      // See [[cart-drawer-audit-findings]] (empty-state morph).
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(last),
        detail: {
          sections: last.sections || {},
          items: last.items || [],
          itemCount: last.item_count || 0,
          source: 'vo-shipping-fee',
          didError: false,
        },
      });
      // Re-read the server cart once after our writes. `_syncing` only coordinates this
      // page; another tab can add a stale/bare fee line concurrently. The verification
      // pass is a no-op when the cart is canonical and reconciles it when it is not.
      fetchAndProcess();
    }

    step();
    return Promise.resolve(true);
  }

  // ---- syncCartAttributes — checkout sidebar mirror ----
  function syncCartAttributes(cart, uniqueDates, fee, config) {
    var symbol = config.currencySymbol || '';
    var next = {};
    var keys = [];

    // This singular legacy attribute is derived from the dated merchandise instead of
    // being written independently by the cart subscription offer. For carts with more
    // than one date, extractUniqueDates() has already sorted them, so this represents the
    // next delivery. Writing an empty value clears stale data when no dated items remain.
    next['Delivery date'] = uniqueDates.length ? uniqueDates[0].key : '';
    keys.push('Delivery date');

    for (var i = 0; i < uniqueDates.length; i++) {
      var d = uniqueDates[i];
      var key = 'Delivery (' + d.display + ')';
      // Price-first: "S$5.00 for Subscription"; a $0 mode reads "Free".
      next[key] = formatFeeLabel(fee.free ? 0 : (modePrice(config, d.mode) || 0), d.mode, symbol);
      keys.push(key);
    }
    var shippingParts = [];
    if (fee.free) {
      next['Shipping'] = 'Free';
    } else {
      if (fee.oneTime.dateCount) shippingParts.push(formatFeeLabel(modePrice(config, 'one-time') || 0, 'one-time', symbol) + ' × ' + fee.oneTime.dateCount);
      if (fee.subscription.dateCount) shippingParts.push(formatFeeLabel(modePrice(config, 'subscription') || 0, 'subscription', symbol) + ' × ' + fee.subscription.dateCount);
      next['Shipping'] = shippingParts.join(', ') || 'Free';
    }
    keys.push('Shipping');

    var body = { attributes: {} };
    var changed = false;
    keys.forEach(function (k) {
      var newVal = next[k];
      if (String(cart.attributes[k] || '') !== String(newVal || '')) { body.attributes[k] = newVal; changed = true; }
    });
    _lastAttributeKeys.forEach(function (k) {
      if (keys.indexOf(k) === -1 && cart.attributes[k] != null && cart.attributes[k] !== '') { body.attributes[k] = ''; changed = true; }
    });
    _lastAttributeKeys = keys;
    if (!changed) return Promise.resolve();
    return cartPost(Theme.routes.cart_update_url, body);
  }

  // ---- renderSummary — cart UI ----
  function renderSummary(cart, uniqueDates, fee, feeItems, config) {
    var shells = document.querySelectorAll('[data-shipping-fee-summary]');
    if (!shells.length) return;

    var feeTotal = feeLineTotal(feeItems);
    var subtotalCents = (cart.items_subtotal_price || 0) - feeTotal;
    var totalCents = cart.total_price || 0;
    var symbol = config.currencySymbol || '';

    var dateListHtml = uniqueDates.map(function (d) {
      var cents = fee.free ? 0 : (modePrice(config, d.mode) || 0);
      return '<li><span class="vo-sf-date">' + d.display + '</span>' +
        '<span class="vo-sf-fee">' + formatFeeLabel(cents, d.mode, symbol) + '</span></li>';
    }).join('');

    var totalFee = fee.free ? 0 : (fee.oneTime.amount + fee.subscription.amount);
    var active = fee.dateCount > 0;
    shells.forEach(function (shell) {
      var root = shell.closest('.vo-cart') || shell.parentElement;
      if (active) {
        root && root.classList.add('vo-shipping-active');
        shell.hidden = false;
        var sub = shell.querySelector('[data-sf-subtotal]');
        var amt = shell.querySelector('[data-sf-amount]');
        var cnt = shell.querySelector('[data-sf-date-count]');
        var tot = shell.querySelector('[data-sf-total]');
        var list = shell.querySelector('[data-sf-date-list]');
        if (sub) sub.textContent = formatMoney(subtotalCents, symbol);
        if (amt) amt.textContent = (fee.free || !totalFee) ? 'Free' : formatMoney(totalFee, symbol);
        if (cnt) cnt.textContent = fee.dateCount + ' date' + (fee.dateCount === 1 ? '' : 's');
        if (tot) tot.textContent = formatMoney(totalCents, symbol);
        if (list) list.innerHTML = dateListHtml;
      } else {
        root && root.classList.remove('vo-shipping-active');
        shell.hidden = true;
      }
    });
  }

  // ---- updateCartBadge — hide the fee item from the header/drawer count ----
  function updateCartBadge(cart, feeItems) {
    var feeQty = feeItems.reduce(function (s, i) { return s + (i.quantity || 0); }, 0);
    var corrected = Math.max(0, (cart.item_count || 0) - feeQty);

    var bubble = document.querySelector('cart-icon [ref="cartBubbleCount"], .cart-bubble__text-count');
    if (bubble) {
      bubble.textContent = corrected < 100 ? String(corrected) : '';
      bubble.classList.toggle('hidden', corrected === 0);
      var bubbleWrap = bubble.closest('.cart-bubble');
      if (bubbleWrap) bubbleWrap.classList.toggle('visually-hidden', corrected === 0);
      var icon = bubble.closest('cart-icon');
      if (icon) icon.classList.toggle('header-actions__cart-icon--has-cart', corrected > 0);
    }
    var drawerCount = document.querySelector('[data-vo-cart-count]');
    if (drawerCount) drawerCount.textContent = String(corrected);
  }

  // ---- processCart ----
  function processCart(cart, opts) {
    var config = getConfig();
    if (!config) return;
    // Need at least one fee variant configured to do anything.
    var hasVariant = (config.oneTime && config.oneTime.variantId) ||
                     (config.subscription && config.subscription.variantId) ||
                     config.variantId;
    if (!hasVariant) return;
    var items = cart ? (cart.items || []) : [];
    var uniqueDates = extractUniqueDates(items, config);
    var feeItems = findFeeItems(items, config);
    var merchSubtotal = (cart.items_subtotal_price || 0) - feeLineTotal(feeItems);
    var fee = calculateFee(uniqueDates, config, merchSubtotal);

    renderSummary(cart || {}, uniqueDates, fee, feeItems, config);
    updateCartBadge(cart || { item_count: 0 }, feeItems);

    if (opts && opts.renderOnly) return; // used for the post-mutation re-render
    if (_syncing) return;
    // syncCartAttributes (checkout sidebar mirror, no morph) then syncFeeProduct, which
    // mutates the fee lines and dispatches the morph event + its own renderOnly re-fill.
    syncCartAttributes(cart, uniqueDates, fee, config)
      .then(function () { return syncFeeProduct(cart, uniqueDates, fee, feeItems, config); })
      .catch(function (e) { _syncing = false; console.warn('[shipping-fee] sync failed', e); });
  }

  function fetchAndProcess() {
    fetch(Theme.routes.cart_url + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(processCart)
      .catch(function (e) { console.warn('[shipping-fee] cart fetch failed', e); });
  }

  // Called when a fee sync finishes (finish/fail). If a cart change arrived mid-sync,
  // re-fetch the latest cart and re-sync so fees/summary/bubble reflect the FINAL state
  // (not the state when the in-flight sync started). Idempotent + coalesces bursts.
  function resyncIfPending() {
    if (!_pendingResync) return;
    _pendingResync = false;
    fetchAndProcess();
  }

  // ---- Triggers ----
  // Chevron toggle for the shipping breakdown (delegated so it survives cart re-renders).
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-sf-toggle]');
    if (!toggle) return;
    var details = toggle.parentElement && toggle.parentElement.querySelector('[data-sf-details]');
    if (!details) return;
    var open = details.hasAttribute('hidden') ? false : true;
    if (open) { details.hidden = true; toggle.setAttribute('aria-expanded', 'false'); }
    else { details.hidden = false; toggle.setAttribute('aria-expanded', 'true'); }
  });

  // Horizon's cart event bus. event.promise resolves with { cart, detail }.
  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (_syncing) {
      // A fee sync is in flight. Don't drop this newer cart change — mark a re-sync
      // pending so finish()/fail() re-fetches the latest cart and re-syncs fees.
      // (Previously this returned silently, so a rapid 2nd change left fees/summary/
      // bubble stale until the next cart event.)
      _pendingResync = true;
      return;
    }
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(function () {
      var promise = event && event.promise;
      if (promise && typeof promise.then === 'function') {
        promise
          .then(function (res) { if (res && res.cart) processCart(res.cart); else fetchAndProcess(); })
          .catch(function () { fetchAndProcess(); });
      } else {
        fetchAndProcess();
      }
    }, 60);
  });

  // First-load render (no event yet).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchAndProcess);
  } else {
    fetchAndProcess();
  }
})();
